/**
 * Assistant « Clôture annuelle » (Comptabilité > Clôture) : récapitulatif des écritures de fin d'année,
 * confirmation, puis résultat. Toutes les écritures sont passées AVANT le verrouillage de décembre, dans
 * une séquence atomique (`cloturerExerciceAtomique`) : en cas d'échec, rien n'est verrouillé.
 */
import { openFormModal } from '@presentation/shell/openFormModal';
import { esc, fmtFCFA } from '@presentation/shell/format';
import type { Ecriture } from '@shared/types/Ecriture';
import type { ExerciceFiscal } from '@shared/types/ExerciceFiscal';
import type { Immobilisation } from '@shared/types/Immobilisation';
import type { ClotureParams } from '@domain/exercices/ClotureAnnuelleService';
import type {
  ClotureAnnuelleInput,
  ClotureAnnuelleResultat,
} from '@application/comptabilite/CloturePeriode';
import {
  GARANTIE_CLOTURE_ANNUELLE,
  avertissementDecembreVerrouilleSeul,
  libelleMois,
  preparerClotureAnnuelle,
  type RecapitulatifCloture,
} from '@application/comptabilite/PreparationClotureAnnuelle';
import { STYLE_AIDE, STYLE_SCROLL, STYLE_TH_LIGNE, type Confirmer, type Notifier } from './panelKit';

export const NOTE_DISTRIBUTION =
  'Distribution de dividendes (si votre assemblée en a décidé une) : elle n’est pas passée par cet assistant. Enregistrez-la en saisie manuelle AVANT de lancer la clôture, car décembre sera ensuite verrouillé.';

export interface ChargementCloture {
  exercice: ExerciceFiscal;
  ecritures: Ecriture[];
  immobilisations: Immobilisation[];
  /** Valeurs suggérées (créances clients, masse salariale, capital, réserves) : l'utilisateur peut les corriger. */
  params: ClotureParams;
  cloturees: string[];
  exerciceCloture: boolean;
}

export interface ClotureAnnuelleDeps {
  peutCloturer: boolean;
  annees: readonly string[];
  anneeInitiale: string;
  charger(annee: string): Promise<ChargementCloture>;
  executer(input: ClotureAnnuelleInput): Promise<ClotureAnnuelleResultat>;
  confirm: Confirmer;
  notify: Notifier;
  apres?(): Promise<void> | void;
}

const P = 'cloture-annuelle';

const CHAMPS_PARAMS: { cle: keyof ClotureParams; libelle: string; aide: string }[] = [
  { cle: 'encours_clients', libelle: 'Créances clients à fin d’année (FCFA)', aide: 'Sert à calculer la provision pour créances douteuses (5 %).' },
  { cle: 'masse_salariale', libelle: 'Masse salariale de l’année (FCFA)', aide: 'Sert à calculer les provisions pour congés payés et indemnités de fin de carrière.' },
  { cle: 'capital_social', libelle: 'Capital social (FCFA)', aide: 'Sert à plafonner la réserve légale (20 % du capital).' },
  { cle: 'reserves_existantes', libelle: 'Réserve légale déjà constituée (FCFA)', aide: 'Réserve déjà mise de côté lors des exercices précédents.' },
];

export function ouvrirClotureAnnuelle(parent: HTMLElement, deps: ClotureAnnuelleDeps): void {
  openFormModal(parent, { title: 'Clôture annuelle', size: 'lg' }, (host, fermer) => {
    void piloter(host, fermer, deps);
  });
}

async function piloter(host: HTMLElement, fermer: () => void, deps: ClotureAnnuelleDeps): Promise<void> {
  let annee = deps.anneeInitiale;
  let chargement: ChargementCloture | null = null;
  let params: ClotureParams = { encours_clients: 0, masse_salariale: 0, capital_social: 0, reserves_existantes: 0 };
  let erreur: string | null = null;
  let recap: RecapitulatifCloture | null = null;
  let termine: ClotureAnnuelleResultat | null = null;
  let enCours = false;

  const calculer = (): void => {
    recap = null;
    erreur = null;
    if (!chargement) return;
    try {
      recap = preparerClotureAnnuelle({
        exercice: chargement.exercice,
        ecritures: chargement.ecritures,
        immobilisations: chargement.immobilisations,
        params,
      });
    } catch (err) {
      erreur = err instanceof Error ? err.message : 'Le récapitulatif n’a pas pu être calculé.';
    }
  };

  const recharger = async (): Promise<void> => {
    try {
      chargement = await deps.charger(annee);
      params = { ...chargement.params };
    } catch (err) {
      chargement = null;
      erreur = err instanceof Error ? err.message : 'Les données de l’exercice n’ont pas pu être lues.';
    }
    calculer();
    rendre();
  };

  const avertissement = (): string | null => {
    if (!chargement) return null;
    if (chargement.exerciceCloture) return `L’exercice ${annee} est déjà clôturé : il n’y a plus rien à faire.`;
    return avertissementDecembreVerrouilleSeul({
      annee,
      cloturees: chargement.cloturees,
      exerciceCloture: chargement.exerciceCloture,
    });
  };

  /** Boutons d'action dans le pied de la fenêtre : toujours visibles, même quand le récapitulatif défile. */
  const modal = host.closest('nexxus-modal');
  const pied = document.createElement('div');
  pied.slot = 'footer';
  pied.style.cssText = 'display:flex;gap:10px;flex-wrap:wrap;';
  modal?.appendChild(pied);

  const rendre = (): void => {
    if (termine) {
      host.innerHTML = `
        <div id="${P}-resultat" role="status">
          <h3 style="margin:0 0 8px;color:var(--marine);font-size:15px;">Clôture ${esc(annee)} terminée</h3>
          <p style="margin:0 0 6px;"><strong>${termine.ecritures_passees}</strong> écriture(s) passée(s), puis ${esc(libelleMois(termine.periode))} verrouillé.</p>
          <p style="margin:0 0 6px;">Résultat net de l’exercice : <strong>${esc(fmtFCFA(termine.resultat_net))}</strong>.</p>
          <p style="margin:0 0 12px;">Sceau de la période : <code class="mono">${esc(termine.sceau.slice(0, 16))}…</code></p>
        </div>
        `;
      pied.innerHTML = `<button type="button" class="btn btn-primary" id="${P}-fermer">Fermer</button>`;
      pied.querySelector(`#${P}-fermer`)?.addEventListener('click', fermer);
      pied.querySelector<HTMLButtonElement>(`#${P}-fermer`)?.focus();
      return;
    }
    const bloque = avertissement();
    const options = deps.annees
      .map((a) => `<option value="${esc(a)}" ${a === annee ? 'selected' : ''}>Exercice ${esc(a)}</option>`)
      .join('');
    const lignes = recap
      ? recap.lignes
          .map(
            (l) => `<tr><th scope="row" style="${STYLE_TH_LIGNE}">${esc(l.libelle)}</th>
              <td class="num">${esc(fmtFCFA(l.montant))}</td><td class="num">${l.nbEcritures}</td></tr>`,
          )
          .join('')
      : '';
    const champs = CHAMPS_PARAMS.map(
      (c) => `<div class="field"><label for="${P}-${c.cle}">${esc(c.libelle)}</label>
        <input id="${P}-${c.cle}" type="number" min="0" step="1" inputmode="numeric" value="${params[c.cle]}" aria-describedby="${P}-${c.cle}-aide" ${deps.peutCloturer ? '' : 'readonly'}>
        <span id="${P}-${c.cle}-aide" style="font-size:12px;color:var(--ardoise);">${esc(c.aide)}</span></div>`,
    ).join('');
    host.innerHTML = `
      <p style="${STYLE_AIDE}">Cet assistant passe d’un seul coup toutes les écritures de fin d’année, puis verrouille décembre.
        <strong id="${P}-garantie">${esc(GARANTIE_CLOTURE_ANNUELLE)}</strong></p>
      <div class="field" style="max-width:260px;margin-bottom:12px;">
        <label for="${P}-exercice">Exercice à clôturer</label>
        <select id="${P}-exercice">${options}</select>
      </div>
      <div id="${P}-alerte" role="alert" ${bloque || erreur ? '' : 'hidden'} class="license-banner reminder">${esc(erreur ?? bloque ?? '')}</div>
      ${
        recap
          ? `<h3 style="font-size:14px;color:var(--marine);margin:0 0 6px;">Ce qui va être passé</h3>
             <div style="${STYLE_SCROLL}"><table class="data" id="${P}-recap">
               <caption class="sr-only">Écritures de fin d’année qui seront passées avant le verrouillage de décembre</caption>
               <thead><tr><th scope="col">Opération</th><th scope="col" class="num">Montant</th><th scope="col" class="num">Écritures</th></tr></thead>
               <tbody>${lignes}</tbody>
               <tfoot><tr><th scope="row" style="${STYLE_TH_LIGNE}">Total des écritures à passer</th><td></td><td class="num" id="${P}-total">${recap.nbEcritures}</td></tr></tfoot>
             </table></div>
             <p id="${P}-distribution" style="${STYLE_AIDE}margin-top:10px;">${esc(NOTE_DISTRIBUTION)}</p>
             <h3 style="font-size:14px;color:var(--marine);margin:12px 0 6px;">Chiffres utilisés pour les provisions et la réserve légale</h3>
             <p style="${STYLE_AIDE}">Ils sont calculés à partir de vos données ; corrigez-les si besoin, le récapitulatif se met à jour.</p>
             <div class="form-grid" style="display:grid;grid-template-columns:1fr 1fr;gap:10px;">${champs}</div>`
          : ''
      }
      ${deps.peutCloturer ? '' : `<p style="${STYLE_AIDE}margin-top:8px;">Lecture seule : la clôture est réservée à l’administrateur, au gestionnaire RH et au comptable.</p>`}`;
    pied.innerHTML = `
      <button type="button" class="btn btn-ghost" id="${P}-annuler">Annuler</button>
      <button type="button" class="btn btn-primary" id="${P}-lancer" ${!deps.peutCloturer || !recap || bloque || enCours ? 'disabled' : ''}>Clôturer l’exercice ${esc(annee)}</button>`;

    pied.querySelector(`#${P}-annuler`)?.addEventListener('click', fermer);
    host.querySelector<HTMLSelectElement>(`#${P}-exercice`)?.addEventListener('change', (e) => {
      annee = (e.target as HTMLSelectElement).value;
      void recharger();
    });
    for (const c of CHAMPS_PARAMS) {
      host.querySelector<HTMLInputElement>(`#${P}-${c.cle}`)?.addEventListener('change', (e) => {
        const n = Number((e.target as HTMLInputElement).value);
        params = { ...params, [c.cle]: Number.isFinite(n) && n >= 0 ? Math.round(n) : 0 };
        calculer();
        rendre();
        host.querySelector<HTMLInputElement>(`#${P}-${c.cle}`)?.focus();
      });
    }
    pied.querySelector(`#${P}-lancer`)?.addEventListener('click', () => void lancer());
  };

  const lancer = async (): Promise<void> => {
    if (!chargement || !recap || enCours || !deps.peutCloturer) return;
    const ok = await deps.confirm({
      title: `Clôturer l’exercice ${annee}`,
      message:
        `${recap.nbEcritures} écriture(s) de fin d’année vont être passées, puis ${libelleMois(`${annee}-12`)} sera verrouillé. ` +
        'Seul un administrateur pourra ensuite le déverrouiller. ' +
        GARANTIE_CLOTURE_ANNUELLE,
      confirmLabel: 'Clôturer',
      danger: true,
      host,
    });
    if (!ok) return;
    enCours = true;
    rendre();
    try {
      termine = await deps.executer({
        exercice: chargement.exercice,
        params,
        ecrituresComplementaires: recap.dotations,
        peutCloturer: deps.peutCloturer,
      });
      deps.notify.success(`Exercice ${annee} clôturé : ${termine.ecritures_passees} écriture(s) passée(s).`);
      await deps.apres?.();
    } catch (err) {
      termine = null;
      erreur = `${err instanceof Error ? err.message : 'La clôture a échoué.'} Rien n’a été verrouillé.`;
      deps.notify.error(erreur);
    } finally {
      enCours = false;
    }
    rendre();
  };

  await recharger();
}
